import React from "react";
import { FiDownload, FiTrash } from "react-icons/fi";
import { CgSpinner } from "react-icons/cg";

import * as types from "types";
import type { ScopeFile } from "services/fileApi/types/ScopeFile";
import useDownloadFiles from "../hooks/useDownloadFiles";
import useStoredChoice from "../hooks/useStoredChoice";
import sortFiles, { FILE_SORT_KEYS } from "../utilities/sortFiles";
import { FILE_VIEW_MODES } from "../utilities/fileViewModes";
import FileItem from "./FileItem";
import FileListToolbar from "./FileListToolbar";
import styles from "../FileExplorer.module.scss";

interface FileListProps {
  chatThread: types.ChatThread;
  files: ScopeFile[];
  deleteFiles: (files: ScopeFile[]) => Promise<void>;
  canDelete: boolean;
}

/**
 * The toolbar and the files, as a table or a gallery, with multi-select (ADR
 * 016). Key it by thread so switching threads resets its state.
 */
export default function FileList({
  chatThread,
  files,
  deleteFiles,
  canDelete,
}: FileListProps) {
  const { downloadFiles, isDownloading } = useDownloadFiles({ chatThread });

  const [searchText, setSearchText] = React.useState("");
  const [sortKey, setSortKey] = useStoredChoice(
    "fileExplorerSort",
    FILE_SORT_KEYS,
    "date"
  );
  const [viewMode, setViewMode] = useStoredChoice(
    "fileExplorerView",
    FILE_VIEW_MODES,
    "list"
  );

  const searchTerm = searchText.trim().toLowerCase();
  const visibleFiles = sortFiles(
    files.filter((file) => file.filename.toLowerCase().includes(searchTerm)),
    sortKey
  );

  const [selectedFileIds, setSelectedFileIds] = React.useState<Set<string>>(
    new Set()
  );
  // Only visible files count as selected. A file the search hides stays in
  // `selectedFileIds`, so clearing the search reselects it.
  const selectedFiles = visibleFiles.filter((file) =>
    selectedFileIds.has(file.fileId)
  );
  const hasSelection = selectedFiles.length > 0;
  const allSelected =
    hasSelection && selectedFiles.length === visibleFiles.length;
  const isIndeterminate = hasSelection && !allSelected;

  // Where a shift-click range starts. An ID, because sorting, searching, and
  // deletes shift indexes.
  const lastClickedFileIdRef = React.useRef<string | null>(null);

  const setFilesSelected = (fileIds: string[], selected: boolean) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      for (const fileId of fileIds) {
        if (selected) {
          next.add(fileId);
        } else {
          next.delete(fileId);
        }
      }
      return next;
    });
  };

  const toggleFileSelection = (fileId: string, shiftKey: boolean) => {
    const visibleFileIds = visibleFiles.map((file) => file.fileId);
    const currentIndex = visibleFileIds.indexOf(fileId);
    // -1 when the last-clicked file is unset or not visible.
    let anchorIndex = -1;
    if (lastClickedFileIdRef.current !== null) {
      anchorIndex = visibleFileIds.indexOf(lastClickedFileIdRef.current);
    }
    const selecting = !selectedFileIds.has(fileId);

    if (shiftKey && anchorIndex !== -1) {
      const start = Math.min(anchorIndex, currentIndex);
      const end = Math.max(anchorIndex, currentIndex);
      setFilesSelected(visibleFileIds.slice(start, end + 1), selecting);
    } else {
      setFilesSelected([fileId], selecting);
    }

    lastClickedFileIdRef.current = fileId;
  };

  const toggleSelectAll = () => {
    setFilesSelected(
      visibleFiles.map((file) => file.fileId),
      !hasSelection
    );
  };

  // `indeterminate` has no HTML attribute. A callback ref also covers the new
  // checkbox that switching views mounts.
  const setSelectAllIndeterminate = React.useCallback(
    (checkbox: HTMLInputElement | null) => {
      if (checkbox) checkbox.indeterminate = isIndeterminate;
    },
    [isIndeterminate]
  );

  // Always visible in the gallery, beside its "Select all" text.
  const selectAllClass =
    hasSelection || viewMode === "gallery"
      ? styles.selectCheckbox
      : `${styles.selectCheckbox} ${styles.hoverOnly}`;
  const selectAll = (
    <span className={styles.checkboxLabel} onClick={toggleSelectAll}>
      <input
        ref={setSelectAllIndeterminate}
        type="checkbox"
        className={selectAllClass}
        checked={allSelected}
        aria-label="Select all files"
        readOnly
      />
      {viewMode === "gallery" && (
        <span className={styles.selectAllText}>Select all</span>
      )}
    </span>
  );

  const fileItems = visibleFiles.map((file) => (
    <FileItem
      key={file.fileId}
      file={file}
      chatThread={chatThread}
      view={viewMode}
      isSelected={selectedFileIds.has(file.fileId)}
      hasSelection={hasSelection}
      onToggleSelect={toggleFileSelection}
      onDelete={(fileToDelete) => void deleteFiles([fileToDelete])}
      canDelete={canDelete}
    />
  ));

  let filesContent: React.ReactNode;
  if (visibleFiles.length === 0) {
    filesContent = <p className={styles.statusMessage}>No matching files</p>;
  } else if (viewMode === "list") {
    filesContent = (
      <div className={styles.fileListScroller}>
        <table className={styles.fileTable}>
          <thead>
            <tr className={styles.headerRow}>
              <th scope="col">Name</th>
              <th scope="col" className={styles.extColumn}>
                Ext
              </th>
              <th scope="col" className={styles.checkboxColumn}>
                {selectAll}
              </th>
              <th
                scope="col"
                className={styles.actionsColumn}
                aria-label="Actions"
              />
            </tr>
          </thead>
          <tbody>{fileItems}</tbody>
        </table>
      </div>
    );
  } else {
    filesContent = (
      <div className={styles.fileListScroller}>
        <div className={styles.galleryHeader}>{selectAll}</div>
        <ul className={styles.gallery}>{fileItems}</ul>
      </div>
    );
  }

  return (
    <>
      <FileListToolbar
        searchText={searchText}
        onSearchTextChange={setSearchText}
        sortKey={sortKey}
        onSortKeyChange={setSortKey}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
      {filesContent}
      {hasSelection && (
        <div className={styles.selectionActions}>
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => void downloadFiles(selectedFiles)}
            disabled={isDownloading}
          >
            {isDownloading ? (
              <CgSpinner className={styles.spinner} aria-hidden="true" />
            ) : (
              <FiDownload aria-hidden="true" />
            )}
            <span>Download Selected ({selectedFiles.length})</span>
          </button>
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => void deleteFiles(selectedFiles)}
            disabled={!canDelete}
          >
            <FiTrash aria-hidden="true" />
            <span>Delete Selected ({selectedFiles.length})</span>
          </button>
        </div>
      )}
    </>
  );
}
